import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import type { WorldDefinition } from "@/levels/worldTypes";
import { WorldRegistry } from "@/levels/WorldRegistry";
import { ProgressStorage } from "@/progress/ProgressStorage";
import { isQaCampaignUnlockActive, isSelectableForQa } from "@/qa/QaMode";
import { createButton } from "@/ui/Button";
import { drawStar } from "@/ui/Star";
import { hexToCss } from "@/ui/colors";
import { drawWorldBackdrop, drawWorldIcon, worldSeed } from "@/ui/worldGraphics";
import { worldRows, fitRowsToHeight, computeStackedScrollLayout, CARD_WIDE_WIDTH, CARD_GAP } from "@/ui/worldCardLayout";
import { attachOrientationGate, destroyLayoutChildren, type OrientationGateHandle } from "@/scenes/OrientationGate";

const CARD_WIDE_HEIGHT = 340;
const CARD_NARROW_HEIGHT = 240;
/** Breathing room kept clear below the last row/card - never crowded flush against the viewport edge. */
const BOTTOM_SAFE_MARGIN = 20;

/** Below this viewport width, cards stack instead of sitting side by side. */
const STACK_BREAKPOINT = 640;

/**
 * A pointer that has moved less than this many px since going down on a
 * world icon still counts as a tap on release; past it, the gesture is a
 * drag/scroll and must never open the world. Generous enough to absorb a
 * real finger's natural wobble on a still tap, small enough to give a
 * one-line scroll gesture that starts directly on an icon away almost
 * immediately.
 */
const WORLD_CARD_TAP_MOVE_THRESHOLD = 10;

/**
 * Sits between the main menu and Level Select: one card per world, each
 * showing that world's progress and unlock state. Built off
 * `WorldRegistry`/`ProgressStorage` alone - adding a third world later
 * needs no changes here, it will simply render as another card.
 *
 * PR27: the stacked (mobile, `width < STACK_BREAKPOINT`) layout used to
 * shrink all five cards down to fit one viewport with no scrolling - down
 * to a 0.5x floor on a typical phone height. It now renders every card at
 * its full, comfortable size and scrolls instead, reusing the exact
 * drag/wheel/mask scroll pattern `LevelSelectScene` already ships (see
 * that file) rather than inventing a second interaction model. The wide
 * (desktop) layout below `buildWideLayout()` is completely untouched -
 * still the original width-wrap + height-shrink-to-fit behavior, which
 * already works well and is shared (via `worldRows()`/`CARD_WIDE_WIDTH`/
 * `CARD_GAP`) with `LeaderboardScene`'s own world filter row.
 */
export class WorldSelectScene extends Phaser.Scene {
  /**
   * `?qa=unlock-campaign` on an allowed QA host - see `isQaCampaignUnlockActive()`.
   * Presentation-only: read once per Scene load, never written back
   * anywhere, never touches `ProgressStorage`.
   */
  private qaUnlockAll = false;

  // Mobile-only vertical scroll state. `maxScroll` is reset to 0 at the
  // top of every `buildLayout()` and only ever set > 0 inside the stacked
  // branch - every handler below guards on `maxScroll <= 0` first, so
  // these are all no-ops on the desktop/wide layout, which never creates
  // `contentContainer` at all.
  private contentContainer!: Phaser.GameObjects.Container;
  private viewportTop = 0;
  private viewportBottom = 0;
  private scrollY = 0;
  private maxScroll = 0;
  private dragging = false;
  private dragStartPointerY = 0;
  private dragStartScroll = 0;
  /**
   * Sticky for the lifetime of one pointer-down->up gesture (reset only
   * when a world icon itself starts a new gesture - see
   * `createWorldCard()`'s own `pointerdown`): once the pointer has moved
   * past `WORLD_CARD_TAP_MOVE_THRESHOLD` since going down, this stays
   * `true` even if the pointer later drifts back near its start position,
   * so a real (if jittery) scroll gesture can never be mistaken for a tap
   * just because of where it happened to end up.
   */
  private hasExceededTapThreshold = false;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("WorldSelectScene");
  }

  create(): void {
    this.qaUnlockAll = isQaCampaignUnlockActive(window.location.hostname, window.location.search);
    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.scrollY = 0;
    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerup", this.onPointerUp, this);
    this.input.on("pointerupoutside", this.onPointerUp, this);
    this.input.on("wheel", this.onWheel, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();
  }

  private teardown(): void {
    this.scale.off("resize", this.onResize, this);
    this.input.off("pointermove", this.onPointerMove, this);
    this.input.off("pointerup", this.onPointerUp, this);
    this.input.off("pointerupoutside", this.onPointerUp, this);
    this.input.off("wheel", this.onWheel, this);
  }

  private onResize(): void {
    this.scrollY = 0;
    this.buildLayout();
  }

  private buildLayout(): void {
    destroyLayoutChildren(this);
    this.maxScroll = 0;
    const { width, height } = this.scale;
    const centerX = width / 2;

    const titleSize = Math.max(26, Math.min(40, width * 0.065));
    this.add
      .text(centerX, height * 0.09, "WORLDS", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    createButton(this, 64, height * 0.09, "BACK", () => this.scene.start("MenuScene"), {
      width: 96,
      height: 40,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });

    const worlds = WorldRegistry.all();
    if (width < STACK_BREAKPOINT) {
      this.buildStackedScrollableLayout(worlds, width, height, centerX);
    } else {
      this.buildWideLayout(worlds, width, height, centerX);
    }
  }

  /**
   * Desktop/wide layout - byte-for-byte the original behavior: `worldRows()`
   * decides the width-only wrap, `fitRowsToHeight()` uniformly shrinks the
   * resulting rows (never below its own 0.5x floor) so a cramped laptop
   * height still fits without clipping. No scrolling here - this is the
   * layout the audit/spec called out as already working well.
   */
  private buildWideLayout(worlds: readonly WorldDefinition[], width: number, height: number, centerX: number): void {
    const rows = worldRows(worlds, width, CARD_WIDE_WIDTH, CARD_GAP);
    const rowCount = rows.length;

    const startY = height * 0.24;
    const usableHeight = height - startY - BOTTOM_SAFE_MARGIN;
    const fit = fitRowsToHeight(rowCount, CARD_WIDE_WIDTH, CARD_WIDE_HEIGHT, CARD_GAP, usableHeight);
    const { cardWidth, cardHeight, gap } = fit;

    let y = startY + cardHeight / 2;
    for (const row of rows) {
      const totalWidth = row.length * cardWidth + (row.length - 1) * gap;
      let x = centerX - totalWidth / 2 + cardWidth / 2;
      for (const world of row) {
        this.createWorldCard(world, x, y, cardWidth, cardHeight);
        x += cardWidth + gap;
      }
      y += cardHeight + gap;
    }
  }

  /**
   * Mobile/stacked layout - every card renders at its full, comfortable
   * size (never shrunk); if the five stacked cards don't all fit the
   * viewport, the list scrolls instead. Structurally identical to
   * `LevelSelectScene`'s own scrollable grid: a background drag-zone
   * (added first, so cards win hit-testing priority), a masked
   * `contentContainer` holding the cards, and the same drag/wheel handlers.
   */
  private buildStackedScrollableLayout(worlds: readonly WorldDefinition[], width: number, height: number, centerX: number): void {
    this.viewportTop = height * 0.16;
    this.viewportBottom = height - BOTTOM_SAFE_MARGIN;
    const viewportHeight = Math.max(1, this.viewportBottom - this.viewportTop);

    const { cardWidth, cardHeight, maxScroll } = computeStackedScrollLayout(worlds.length, width, viewportHeight, CARD_NARROW_HEIGHT, CARD_GAP);

    const dragZone = this.add
      .zone(0, this.viewportTop, width, viewportHeight)
      .setOrigin(0, 0)
      .setInteractive();
    dragZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragStartPointerY = pointer.y;
      this.dragStartScroll = this.scrollY;
    });

    this.contentContainer = this.add.container(0, this.viewportTop);

    let y = 0;
    worlds.forEach((world) => {
      const cardCenterY = y + cardHeight / 2;
      this.createWorldCard(world, centerX, cardCenterY, cardWidth, cardHeight, this.contentContainer);
      y += cardHeight + CARD_GAP;
    });

    this.maxScroll = maxScroll;
    this.scrollY = Math.min(this.scrollY, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;

    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, this.viewportTop, width, viewportHeight);
    this.contentContainer.setMask(maskShape.createGeometryMask());
  }

  /**
   * `parent` is only ever passed from the scrollable mobile layout - the
   * card is nested inside `contentContainer` (so it scrolls/masks with the
   * rest of the list) instead of sitting directly on the scene. Desktop
   * cards pass no `parent` and keep their original scene-level placement.
   */
  private createWorldCard(world: WorldDefinition, x: number, y: number, w: number, h: number, parent?: Phaser.GameObjects.Container): void {
    const summary = ProgressStorage.getWorldSummary(world.id);
    // Presentation-only: whether this card renders/behaves as unlocked.
    // `summary.isCompleted`/`completedLevels`/`stars` below are always
    // the real, unmodified `ProgressStorage` values - only this gating
    // decision is affected by the QA override.
    const isUnlocked = isSelectableForQa(summary.isUnlocked, this.qaUnlockAll);
    const theme = world.theme;

    const container = this.add.container(x, y);
    if (parent) parent.add(container);
    const g = this.add.graphics();

    const fillColor = isUnlocked ? theme.primary : 0x14161f;
    const borderColor = isUnlocked ? theme.accent : GameConfig.colors.gridLine;

    g.fillStyle(fillColor, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 20);
    g.lineStyle(isUnlocked ? 2.5 : 1.5, borderColor, isUnlocked ? 1 : 0.6);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 20);
    container.add(g);

    // Subtle secondary tint band along the top, so each world reads as
    // visually distinct even before reading any text.
    if (isUnlocked) {
      const band = this.add.graphics();
      band.fillStyle(theme.secondary, 0.5);
      band.fillRoundedRect(-w / 2, -h / 2, w, h * 0.32, { tl: 20, tr: 20, bl: 0, br: 0 });
      container.add(band);
    }

    // Faint backdrop (stars/arcs), confined to the card and drawn before
    // the icon/text so it never competes with them for attention.
    if (isUnlocked) {
      const backdrop = this.add.graphics();
      drawWorldBackdrop(backdrop, theme, { x: -w / 2, y: -h / 2, width: w, height: h }, worldSeed(world.id));
      container.add(backdrop);
    }

    const nameY = -h / 2 + h * 0.13;
    const nameSize = Math.max(20, Math.min(28, w * 0.09));
    const nameText = this.add
      .text(0, nameY, world.name, {
        fontFamily: "Arial, sans-serif",
        fontSize: `${nameSize}px`,
        fontStyle: "bold",
        color: isUnlocked ? "#f5f7fa" : "#4a5168",
      })
      .setOrigin(0.5);
    container.add(nameText);

    // The world icon itself - always drawn (dimmed when locked), so the
    // player recognizes the world even before it's reachable.
    const iconY = -h / 2 + h * 0.42;
    const iconRadius = Math.min(h * 0.135, w * 0.13);
    const iconGraphics = this.add.graphics().setAlpha(isUnlocked ? 1 : 0.35);
    drawWorldIcon(iconGraphics, theme, 0, iconY, iconRadius);
    container.add(iconGraphics);

    if (!isUnlocked) {
      this.drawLockBadge(container, iconRadius * 0.6, iconY + iconRadius * 0.6);
      const hint = this.add
        .text(0, -h / 2 + h * 0.72, `${this.lockedHintText(world)}\nTO UNLOCK`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: "#8b93a7",
          align: "center",
        })
        .setOrigin(0.5);
      container.add(hint);
      return;
    }

    const statusText = this.add
      .text(0, -h / 2 + h * 0.24, summary.isCompleted ? "COMPLETED" : "IN PROGRESS", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: summary.isCompleted ? hexToCss(GameConfig.colors.gateOpen) : "#8b93a7",
      })
      .setOrigin(0.5);
    container.add(statusText);

    const levelsY = -h / 2 + h * 0.62;
    const levelsText = this.add
      .text(0, levelsY, `${summary.completedLevels} / ${summary.totalLevels} levels`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);
    container.add(levelsText);

    // One row of small stars per level would get cramped at 5 levels -
    // show the total star count next to a single star icon instead.
    const starsY = -h / 2 + h * 0.72;
    const starsGraphics = this.add.graphics();
    drawStar(starsGraphics, -22, starsY, 9, GameConfig.colors.star);
    container.add(starsGraphics);

    const starsText = this.add
      .text(6, starsY, `${summary.stars} / ${summary.maxStars}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0, 0.5);
    container.add(starsText);

    const hitArea = new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h);
    g.setInteractive({ hitArea, hitAreaCallback: Phaser.Geom.Rectangle.Contains, useHandCursor: true });
    g.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Phaser's input is `topOnly` by default, so when the finger/cursor
      // is directly over an icon the background drag-zone's own
      // `pointerdown` (`buildStackedScrollableLayout()`) never fires -
      // without also driving the shared drag state from here, starting a
      // scroll gesture ON an icon would be silently ignored by
      // `onPointerMove`'s `if (!this.dragging) return` guard below, and
      // only the icon's own tap-open would ever run. A no-op on the
      // desktop wide layout (`maxScroll` stays 0 there).
      this.dragging = true;
      this.dragStartPointerY = pointer.y;
      this.dragStartScroll = this.scrollY;
      this.hasExceededTapThreshold = false;

      this.tweens.add({
        targets: container,
        scale: 0.97,
        duration: 90,
        yoyo: true,
        onComplete: () => {
          // A drag/scroll gesture that started on this icon (movement past
          // the tap threshold at any point since this exact pointerdown -
          // see `onPointerMove`) must never open the world on release,
          // wherever the pointer ends up.
          if (this.hasExceededTapThreshold) return;
          // Guards a queued tap-open tween that completes after the card
          // has scrolled out of view (or the list has since rebuilt) -
          // mirrors LevelSelectScene's own `isTileVisible()`. Desktop
          // cards never pass a `parent`, so this never applies to them.
          if (parent && !this.isCardVisible(container, h)) return;
          this.scene.start("LevelSelectScene", { worldId: world.id });
        },
      });
    });
  }

  private isCardVisible(card: Phaser.GameObjects.Container, cardHeight: number): boolean {
    const worldY = this.contentContainer.y + card.y;
    const half = cardHeight / 2;
    return worldY + half >= this.viewportTop && worldY - half <= this.viewportBottom;
  }

  /**
   * The locked-card hint text, derived from `world.unlock` rather than
   * hardcoded - so a locked Mars card correctly reads "COMPLETE THE
   * EARTH" instead of the previous, hardcoded "COMPLETE THE MOON" (which
   * only ever happened to be right while Earth was the only lockable
   * world). Generic for Saturn/Sun too, with no per-world conditional.
   */
  private lockedHintText(world: WorldDefinition): string {
    if (world.unlock.type !== "requires-world-complete") return "LOCKED";
    const name = WorldRegistry.byId(world.unlock.worldId)?.name ?? "THE PREVIOUS WORLD";
    // World display names are inconsistent about carrying their own
    // article ("THE MOON" vs. "EARTH"/"MARS") - never double it up.
    const article = /^the\s/i.test(name) ? "" : "THE ";
    return `COMPLETE ${article}${name}`;
  }

  /** Small lock badge, overlaid on the corner of a dimmed (locked) world icon. */
  private drawLockBadge(container: Phaser.GameObjects.Container, x: number, y: number): void {
    const g = this.add.graphics();
    g.fillStyle(0x14161f, 1);
    g.fillCircle(x, y, 15);
    g.lineStyle(2, 0x8b93a7, 1);
    g.strokeRoundedRect(x - 8, y - 2, 16, 13, 3);
    g.beginPath();
    g.arc(x, y - 2, 6.5, Math.PI, Math.PI * 2, false);
    g.strokePath();
    container.add(g);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragging) return;
    // Tracked independent of `maxScroll` (which is 0 on the desktop wide
    // layout, and can legitimately be 0 on mobile too if every card
    // already fits) - a card's own tap-vs-drag decision must not depend on
    // whether the list happens to be scrollable right now.
    if (!this.hasExceededTapThreshold && pointer.getDistance() > WORLD_CARD_TAP_MOVE_THRESHOLD) {
      this.hasExceededTapThreshold = true;
    }
    if (this.maxScroll <= 0) return;
    const delta = pointer.y - this.dragStartPointerY;
    this.scrollY = Phaser.Math.Clamp(this.dragStartScroll - delta, 0, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;
  }

  private onPointerUp(): void {
    this.dragging = false;
  }

  private onWheel(_pointer: Phaser.Input.Pointer, _objects: unknown, _dx: number, dy: number): void {
    if (this.maxScroll <= 0) return;
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;
  }
}
